import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmProvider } from './components/shared/Confirm';
import AppLayout from './components/shared/AppLayout';

// Public
import LoginPage from './pages/LoginPage';
import BookingPage from './pages/public/BookingPage';

// Admin
import DashboardPage from './pages/admin/DashboardPage';
import AppointmentsPage from './pages/admin/AppointmentsPage';
import CalendarPage from './pages/admin/CalendarPage';
import ServicesPage from './pages/admin/ServicesPage';
import WorkersPage from './pages/admin/WorkersPage';
import ClientsPage from './pages/admin/ClientsPage';
import ReportsPage from './pages/admin/ReportsPage';
import FinancePage from './pages/admin/FinancePage';
import BusinessPage from './pages/admin/BusinessPage';
import BranchesPage from './pages/admin/BranchesPage';
import AdvancedReportsPage from './pages/admin/AdvancedReportsPage';
import SupportPage from './pages/admin/SupportPage';

// Barber
import BarberCalendarPage from './pages/barber/BarberCalendarPage';
import BarberAppointmentsPage from './pages/barber/BarberAppointmentsPage';

// Superadmin
import SuperDashboard from './pages/superadmin/SuperDashboard';
import BusinessesPage from './pages/superadmin/BusinessesPage';
import PlansPage from './pages/superadmin/PlansPage';
import UsersPage from './pages/superadmin/UsersPage';
import SuperReportsPage from './pages/superadmin/SuperReportsPage';
import TicketsPage from './pages/superadmin/TicketsPage';
import SubscriptionsPage from './pages/superadmin/SubscriptionsPage';
import PaymentsPage from './pages/superadmin/PaymentsPage';
import PlatformFinancePage from './pages/superadmin/PlatformFinancePage';
import PermissionsPage from './pages/superadmin/PermissionsPage';
import IntegrationsPage from './pages/superadmin/IntegrationsPage';
// Bitacora de WhatsApp del negocio. Es solo para el administrador del negocio:
// el super admin no la ve.
import NotificationsPage from './pages/shared/NotificationsPage';
import UsagePage from './pages/superadmin/UsagePage';
import ModuloPendiente from './pages/superadmin/ModuloPendiente';

// Modulos del panel aun no construidos: cada uno explica que hara y que necesita.
const MODULOS_PENDIENTES = {
  billing:       { icono: '🧾', titulo: 'Facturación', descripcion: 'Facturas emitidas y pendientes de los negocios',
                   incluye: ['Facturas emitidas y pendientes', 'Notas crédito', 'Impuestos y configuración fiscal', 'Plantillas de factura', 'Exportar PDF'],
                   requiere: ['Tablas de facturas y sus detalles', 'Datos fiscales del negocio (NIT, régimen)', 'Generador de PDF'] },
  commissions:   { icono: '％', titulo: 'Comisiones', descripcion: 'Comisiones por negocio y por plan',
                   incluye: ['Comisión por negocio y por plan', 'Pagos de comisiones', 'Historial'],
                   requiere: ['Tabla de comisiones', 'Reglas de cálculo definidas por ti'] },
  growth:        { icono: '📈', titulo: 'Crecimiento', descripcion: 'Evolución de negocios y usuarios en el tiempo',
                   incluye: ['Crecimiento diario, semanal, mensual y anual', 'Tendencias'],
                   requiere: ['Nada nuevo: se construye con las fechas de registro que ya existen'] },
  campaigns:     { icono: '📣', titulo: 'Campañas', descripcion: 'Campañas de email, WhatsApp y SMS',
                   incluye: ['Email marketing', 'Campañas por WhatsApp', 'SMS y notificaciones push'],
                   requiere: ['Tabla de campañas', 'Proveedor de envío masivo'] },
  promotions:    { icono: '🎁', titulo: 'Promociones', descripcion: 'Cupones, descuentos y referidos',
                   incluye: ['Cupones y descuentos', 'Programas de referidos', 'Fidelización'],
                   requiere: ['Tablas de cupones y su uso'] },
  knowledge:     { icono: '📚', titulo: 'Base de Conocimiento', descripcion: 'Manuales, tutoriales y preguntas frecuentes',
                   incluye: ['Categorías y artículos', 'Tutoriales en video', 'Preguntas frecuentes'],
                   requiere: ['Tablas de artículos y categorías', 'Editor de contenido'] },
  settings:      { icono: '⚙️', titulo: 'Parámetros', descripcion: 'Configuración general de la plataforma',
                   incluye: ['Nombre y marca', 'Dominio, idioma y moneda', 'Zona horaria'],
                   requiere: ['Tabla de configuración global'] },
  security:      { icono: '🛡️', titulo: 'Seguridad', descripcion: 'Autenticación y auditoría de accesos',
                   incluye: ['Doble factor (2FA)', 'Sesiones activas', 'Lista de IP permitidas', 'Políticas de contraseña'],
                   requiere: ['Tabla de sesiones', 'Librería de 2FA'] },
  logs:          { icono: '📜', titulo: 'Logs del Sistema', descripcion: 'Eventos, errores y auditoría',
                   incluye: ['Eventos y errores del sistema', 'Accesos de usuarios', 'Historial de cambios'],
                   requiere: ['Tabla de auditoría y registro de cada acción'] },
  backups:       { icono: '☁️', titulo: 'Backups', descripcion: 'Copias de seguridad de la base de datos',
                   incluye: ['Copias automáticas y manuales', 'Restauración', 'Programación'],
                   requiere: ['Acceso a un almacenamiento externo', 'Tarea programada de respaldo'] },
};

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'var(--color-gold)', fontFamily:'var(--font-display)', fontSize:'1.2rem' }}>Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/unauthorized" replace />;
  return children;
}

// Inicio del panel de negocio: muestra el Dashboard solo si el plan tiene ese
// modulo activo; si no, entra a la primera seccion que si tenga. Clientes es
// el ultimo recurso porque va incluido en todos los planes.
function AdminHome() {
  const { hasPlan } = useAuth();
  if (hasPlan('has_dashboard'))    return <DashboardPage />;
  if (hasPlan('has_calendar'))     return <Navigate to="/admin/calendar" replace />;
  if (hasPlan('has_appointments')) return <Navigate to="/admin/appointments" replace />;
  return <Navigate to="/admin/clients" replace />;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/superadmin" replace />;
  if (user.role === 'barbero') return <Navigate to="/barber/calendar" replace />;
  return <Navigate to="/admin" replace />;
}

function App() {
  return (
    <AuthProvider>
      {/* Las confirmaciones se dibujan como modal, no con el dialogo del
          navegador. Envuelve todo, incluida la reserva publica. */}
      <ConfirmProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/book/:slug" element={<BookingPage />} />
          <Route path="/" element={<RoleHome />} />
          <Route path="/unauthorized" element={
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', gap:'1rem' }}>
              <h1 style={{ color:'var(--color-gold)' }}>Sin acceso</h1>
              <p style={{ color:'var(--color-text-muted)' }}>No tienes permisos para esta sección.</p>
              <a href="/" className="btn btn-primary">Volver al inicio</a>
            </div>
          } />

          {/* Admin / Admin General routes */}
          <Route path="/admin" element={
            <ProtectedRoute roles={['admin', 'admin_general']}>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminHome />} />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="workers" element={<WorkersPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="business" element={<BusinessPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="hours" element={<BusinessPage defaultTab="horarios" />} />
            <Route path="branches" element={<BranchesPage />} />
            <Route path="advanced-reports" element={<AdvancedReportsPage />} />
            <Route path="support" element={<SupportPage />} />
          </Route>

          {/* Barbero routes */}
          <Route path="/barber" element={
            <ProtectedRoute roles={['barbero']}>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="calendar" replace />} />
            <Route path="calendar" element={<BarberCalendarPage />} />
            <Route path="appointments" element={<BarberAppointmentsPage />} />
            <Route path="support" element={<SupportPage />} />
          </Route>

          {/* Super Admin routes */}
          <Route path="/superadmin" element={
            <ProtectedRoute roles={['super_admin']}>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<SuperDashboard />} />
            <Route path="businesses" element={<BusinessesPage />} />
            <Route path="plans" element={<PlansPage />} />
            <Route path="permissions" element={<PermissionsPage />} />
            {/* Notificaciones de la plataforma vive como pestaña de
                Integraciones; la ruta se mantiene por si hay enlaces viejos. */}
            <Route path="integrations"  element={<IntegrationsPage />} />
            <Route path="notifications" element={<IntegrationsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="reports" element={<SuperReportsPage />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            {/* Financiero agrupa ingresos y gastos. Las rutas antiguas se
                mantienen y abren la pestaña que les corresponde. */}
            {/* Consumo real de servidores, base de datos y sesiones de WhatsApp */}
            <Route path="usage" element={<UsagePage />} />
            <Route path="finance"  element={<PlatformFinancePage defaultTab="resumen" />} />
            <Route path="income"   element={<PlatformFinancePage defaultTab="ingresos" />} />
            <Route path="expenses" element={<PlatformFinancePage defaultTab="gastos" />} />
            {/* Modulos del panel pendientes de construir */}
            {Object.entries(MODULOS_PENDIENTES).map(([ruta, props]) => (
              <Route key={ruta} path={ruta} element={<ModuloPendiente {...props} />} />
            ))}
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
